import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Text, Platform, TouchableOpacity, Image, Animated } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import FormHeader from '../components/FormHeader';
import FormInput from '../components/FormInput';
import PrimaryButton from '../components/PrimaryButton';
import DividerText from '../components/DividerText';
import CheckboxRow from '../components/CheckboxRow';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const GettingStartedScreen = () => {
  const navigation = useNavigation();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const { palette, mode, autoUpdate } = useTheme();

  // Animation values
  const formScale = useRef(new Animated.Value(0.85)).current;
  const formOpacity = useRef(new Animated.Value(0)).current;
  const formRotate = useRef(new Animated.Value(-2)).current;
  const leftCharX = useRef(new Animated.Value(-250)).current;
  const leftCharOpacity = useRef(new Animated.Value(0)).current;
  const leftCharRotate = useRef(new Animated.Value(-15)).current;
  const rightCharX = useRef(new Animated.Value(250)).current;
  const rightCharOpacity = useRef(new Animated.Value(0)).current;
  const rightCharRotate = useRef(new Animated.Value(15)).current;
  
  // Staggered form element animations
  const headerOpacity = useRef(new Animated.Value(0)).current;
  const headerY = useRef(new Animated.Value(30)).current;
  const inputsOpacity = useRef(new Animated.Value(0)).current;
  const inputsY = useRef(new Animated.Value(30)).current;
  const dividerOpacity = useRef(new Animated.Value(0)).current;
  const socialOpacity = useRef(new Animated.Value(0)).current;
  const socialScale = useRef(new Animated.Value(0.9)).current;
  const checkboxOpacity = useRef(new Animated.Value(0)).current;
  const checkboxY = useRef(new Animated.Value(20)).current;

  useEffect(() => {
    // Main form entrance animation with bounce
    Animated.parallel([
      Animated.spring(formScale, {
        toValue: 1,
        tension: 45,
        friction: 7,
        useNativeDriver: true,
      }),
      Animated.timing(formOpacity, {
        toValue: 1,
        duration: 450,
        useNativeDriver: true,
      }),
      Animated.spring(formRotate, {
        toValue: 0,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Left character animation - swing in from left
    Animated.sequence([
      Animated.delay(150),
      Animated.parallel([
        Animated.spring(leftCharX, {
          toValue: 0,
          tension: 35,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(leftCharOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.spring(leftCharRotate, {
          toValue: 0,
          tension: 30,
          friction: 6,
          useNativeDriver: true,
        }),
      ]),
    ]).start();

    // Right character animation - swing in from right
    Animated.sequence([
      Animated.delay(300),
      Animated.parallel([
        Animated.spring(rightCharX, {
          toValue: 0,
          tension: 35,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(rightCharOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.spring(rightCharRotate, {
          toValue: 0,
          tension: 30,
          friction: 6,
          useNativeDriver: true,
        }),
      ]),
    ]).start();

    // Staggered form element animations
    const staggerDelay = 80;
    
    Animated.sequence([
      Animated.delay(250),
      Animated.parallel([
        Animated.timing(headerOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(headerY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(250 + staggerDelay),
      Animated.parallel([
        Animated.timing(inputsOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(inputsY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(250 + staggerDelay * 2),
      Animated.timing(dividerOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
    ]).start();

    Animated.sequence([
      Animated.delay(250 + staggerDelay * 3),
      Animated.parallel([
        Animated.timing(socialOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(socialScale, { toValue: 1, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(250 + staggerDelay * 4),
      Animated.parallel([
        Animated.timing(checkboxOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(checkboxY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();
  }, []);

  const handleSubmit = async () => {
        if (loading) return;
        if (email === '' || password === '' || confirmPassword === '' || fullName === '') {
          setError('Please fill in all fields');
          return;
        }
        if (email.includes('@') === false) {
          setError('Please enter a valid email address');
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        if (!agreeTerms) {
          setError('You must agree to the terms and conditions');
          return;
        }

        setLoading(true);
        setError('');
        
        // Split fullName for register function
        const nameParts = fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const { user, error } = await register(email, password, firstName, lastName, mode, autoUpdate);
    
        if (error) {
          setError(error);
          setLoading(false);
        }
        
        // Success - navigation will be handled automatically by AuthContext's onAuthStateChanged
        // No need to manually navigate here
  };

  const formRotateInterpolate = formRotate.interpolate({
    inputRange: [-2, 0],
    outputRange: ['-2deg', '0deg'],
  });

  const leftCharRotateInterpolate = leftCharRotate.interpolate({
    inputRange: [-15, 0],
    outputRange: ['-15deg', '0deg'],
  });

  const rightCharRotateInterpolate = rightCharRotate.interpolate({
    inputRange: [0, 15],
    outputRange: ['0deg', '15deg'],
  });

  return (
    <View style={styles.container}>
      <Animated.View 
        style={[
          styles.formContainer,
          {
            opacity: formOpacity,
            transform: [
              { scale: formScale },
              { rotate: formRotateInterpolate },
            ],
          }
        ]}
      >
        {/* Left Character */}
        <Animated.View 
          style={[
            styles.leftCharacter,
            {
              opacity: leftCharOpacity,
              transform: [
                { translateX: leftCharX },
                { rotate: leftCharRotateInterpolate },
              ],
            }
          ]}
        >
          <Image
            source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HighRisk_v01_SL.png')}
            style={styles.characterImageLeft}
            resizeMode="contain"
          />
        </Animated.View>

        {/* Right Character */}
        <Animated.View 
          style={[
            styles.rightCharacter,
            {
              opacity: rightCharOpacity,
              transform: [
                { translateX: rightCharX },
              ],
            }
          ]}
        >
          <Image
            source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Waving_v01_SL.png')}
            style={styles.characterImageRight}
            resizeMode="contain"
          />
        </Animated.View>

        <Animated.View style={{ opacity: headerOpacity, transform: [{ translateY: headerY }] }}>
          <FormHeader title="Let's Get Started" subtitle="Please enter your credentials to sign up." />
        </Animated.View>

          <View style={styles.inputSection}>
            <Animated.View 
              style={[
                styles.formInputContainer,
                { opacity: inputsOpacity, transform: [{ translateY: inputsY }] }
              ]}
            >
              <FormInput placeholder="Full Name" value={fullName} onChangeText={setFullName} editable={!loading} returnKeyType="next" />
              <FormInput placeholder="Email" value={email} onChangeText={setEmail} keyboardType="email-address" autoCapitalize="none" editable={!loading} returnKeyType="next" />
              <FormInput placeholder="Password" value={password} onChangeText={setPassword} secureTextEntry editable={!loading} returnKeyType="next" />
              <FormInput placeholder="Re-enter Password" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry editable={!loading} onSubmit={handleSubmit} />
              {error && <Text style={styles.errorText}>{error}</Text>}
              <PrimaryButton title={loading ? 'Loading...' : 'Sign Up'} onPress={handleSubmit} loading={loading} />

              {/* Switch to Login - right below signup button */}
              <TouchableOpacity onPress={() => navigation.navigateTo('Login')} style={styles.switchAuthLink}>
                <Text style={styles.switchAuthText}>
                  Already have an account? <Text style={styles.switchAuthHighlight}>Sign In</Text>
                </Text>
              </TouchableOpacity>
            </Animated.View>
          
          <Animated.View style={{ opacity: dividerOpacity }}>
          <DividerText text="or continue with" />
          </Animated.View>

          {/* Social Sign In Buttons */}
          <Animated.View 
            style={[
              styles.socialButtonsContainer,
              { opacity: socialOpacity, transform: [{ scale: socialScale }] }
            ]}
          >
            <TouchableOpacity style={styles.socialButton} onPress={() => setError('Google sign up will be implemented')} disabled={loading}>
              <Text style={styles.socialButtonText}>Sign Up with Google</Text>
              <Image 
                source={require('../assets/icons/google.png')} 
                style={styles.socialIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
            
            <TouchableOpacity style={styles.socialButton} onPress={() => setError('Apple sign up will be implemented')} disabled={loading}>
              <Text style={styles.socialButtonText}>Sign Up with Apple</Text>
              <Image 
                source={require('../assets/icons/apple.png')} 
                style={styles.socialIconApple}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </Animated.View>

          <Animated.View 
            style={[
              styles.checkboxGroup,
              { opacity: checkboxOpacity, transform: [{ translateY: checkboxY }] }
            ]}
          >
            <CheckboxRow
              checked={agreeTerms}
              onToggle={() => setAgreeTerms(!agreeTerms)}
              label={(
                <Text style={[styles.termsText ]}>
                  I acknowledge & agree to the <Text style={{ color: palette.retroCyan }}>Terms & Conditions</Text>
                </Text>
              )}
            />

            <CheckboxRow label="Remember me" checked={rememberMe} onToggle={() => setRememberMe(!rememberMe)} />
          </Animated.View>

          </View>
      </Animated.View>
      {loading && (
        <View style={styles.loadingOverlay} pointerEvents="auto">
          <View style={styles.loadingCard}>
            <Text style={styles.loadingText}>Creating your account…</Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 48,
    paddingTop: 160,
    paddingBottom: 60,
    overflow: 'hidden',
  },
  formContainer: {
    maxWidth: 700,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 32,
    backgroundColor: '#FF4747',
    borderRadius: 40,
    paddingVertical: 60,
    zIndex: 5,
    position: 'relative',
      ...Platform.select({
          web: {
              boxShadow: '5px 7px 0px rgba(0,0,0,1)',
          },
          default: {
              shadowColor: '#000000',
              shadowOffset: { width: 5, height: 7 },
              shadowOpacity: 1,
              shadowRadius: 0,
              elevation: 8,
          },
      }),
  },
  inputSection: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    width: '100%',
    paddingHorizontal: 100,
    marginTop: 12,
  },
  formInputContainer: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    gap: 16,
    marginBottom: 38,
  },
  row2: {
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
    ...Platform.select({
      web: { display: 'inline-flex' },
      default: { flexDirection: 'row' },
    }),
    flexDirection: 'row',
  },
  flex1: {
    flex: 1,
  },
  socialButtonsContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 16,
    marginTop: 32,
    marginBottom: 32,
  },
  socialButton: {
  flex: 1,
    height: 52,
    backgroundColor: '#000',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    position: 'relative',
    ...Platform.select({
      web: {
        boxShadow: '3px 3px 0px #f1f1f1',
      }
    }),
  },
  socialButtonText: {
    textAlign: 'center',
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Chivo',
    fontWeight: '600',
    lineHeight: 20,
    ...Platform.select({
      web: {
        textShadow: '2px 2px 1px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,1)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 1,
      },
    }),
  },
  socialIcon: {
    width: 20,
    height: 20,
  },
    socialIconApple: {
    width: 25,
    height: 25,
  },
  checkboxGroup: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  termsText: {
    color: '#fff',
    fontSize: 13,
    fontFamily: 'Chivo',
    fontWeight: '500',
    lineHeight: 16,
      ...Platform.select({
      web: {
          textShadow: '2px 2px 0px rgba(0,0,0,1)',
      },
      default: {
          textShadowColor: 'rgba(0,0,0,1)',
          textShadowOffset: { width: 2, height: 2 },
          textShadowRadius: 1,
      },
  }),
  },
  errorText: {
    color: 'red',
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '300',
    textAlign: 'center',
    width: '100%',
    lineHeight: 16,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingCard: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(20,20,20,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)'
  },
  loadingText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'Public Sans',
    fontWeight: '600',
  },
  leftCharacter: {
    position: 'absolute',
    left: '-60%',
    bottom: '-35%',
    zIndex: -1,
  },
  rightCharacter: {
    position: 'absolute',
    right: '-55%',
    bottom: '-10%',
    zIndex: 1,
  },
  characterImageLeft: {
    width: 750,
    height: 750,
  },
  characterImageRight: {
    width: 580,
    height: 580,
  },
  switchAuthLink: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginTop: 12,
  },
  switchAuthText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.8)',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 1px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,1)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 1,
      },
    }),
  },
  switchAuthHighlight: {
    color: '#F7DF7B',
    fontWeight: '700',
  },
});

export default GettingStartedScreen;
